import {
	ActionBar,
	EditableTagField,
	Input,
	SpinnerWithBackground,
	UnsavedChangesDialog,
	useUnsavedChangesWarning,
} from '@viapeople/components'
import { FormElementWrapper } from '@viapeople/components/src/components/FormElementWrapper'
import MarkdownEditor from '@viapeople/components/src/components/MarkdownEditor'
import { useForm } from 'react-hook-form'
import type { EmailTemplateFormInput } from '../types'

interface EmailTemplateFormProps {
	defaultValues: EmailTemplateFormInput
	values: EmailTemplateFormInput
	isSubmitting?: boolean
	onCancel: () => void
	onSubmit: (data: EmailTemplateFormInput) => Promise<void>
	onChange: (key: string, value: string | string[]) => void
}
export function EmailTemplateForm({
	defaultValues,
	values,
	isSubmitting,
	onCancel,
	onSubmit,
	onChange,
}: EmailTemplateFormProps) {
	const {
		register,
		handleSubmit,
		formState: { errors, isDirty },
	} = useForm<EmailTemplateFormInput>({
		defaultValues,
		mode: 'onChange',
		values,
	})

	const { isBlocked, confirmLeave, cancelLeave } = useUnsavedChangesWarning(isDirty)

	return (
		<form onSubmit={handleSubmit((data) => onSubmit(data))} className='pb-24'>
			<div className='gap-4 flex flex-col'>
				<div className='flex gap-4'>
					<Input
						label='Name'
						value={values.name}
						placeholder='Enter the template name'
						{...register('name', { required: 'Template name is required' })}
						hint={errors.name?.message}
						variant={errors.name ? 'error' : 'default'}
						disabled={isSubmitting}
						onChange={(event) => onChange('name', event.target.value)}
					/>
					<Input
						label='Subject'
						value={values.subject}
						placeholder='Fill in the subject of the email'
						{...register('subject', { required: 'Subject is required' })}
						hint={errors.subject?.message}
						variant={errors.subject ? 'error' : 'default'}
						disabled={isSubmitting}
						onChange={(event) => onChange('subject', event.target.value)}
					/>
				</div>

				<div className='space-y-2'>
					<EditableTagField
						label='Tags'
						value={values.tags?.map((tag) => ({ id: tag, label: tag })) || []}
						onChange={(newTags) =>
							onChange(
								'tags',
								newTags.map((tag) => tag.label),
							)
						}
						placeholder='Type and press Enter to add tags'
						hint='Click a suggested tag below or type your own and press Enter.'
					/>
					<div className='flex gap-2'>
						{['nps', 'pec', 'loops'].map((tag) => {
							const alreadyAdded = values.tags?.includes(tag)
							return (
								<button
									key={tag}
									type='button'
									disabled={alreadyAdded}
									className={`px-2 py-1 text-xs rounded border ${alreadyAdded ? 'bg-neutral-100 text-neutral-400 border-neutral-200 cursor-default' : 'bg-white text-neutral-700 border-neutral-300 hover:bg-neutral-50 cursor-pointer'}`}
									onClick={() => {
										if (!alreadyAdded) {
											onChange('tags', [...(values.tags || []), tag])
										}
									}}>
									+ {tag}
								</button>
							)
						})}
					</div>
				</div>

				<FormElementWrapper
					label={'Body'}
					hint={errors.body?.message}
					variant={errors.body ? 'error' : 'default'}
					disabled={isSubmitting}>
					<MarkdownEditor
						defaultValue={values.body}
						onChangeMarkdown={(markdown) => onChange('body', markdown)}
						placeholder='Fill in the body of the email'
						rows={20}
					/>
				</FormElementWrapper>
			</div>

			<ActionBar
				rightActions={[
					{
						variant: 'secondary',
						label: 'Discard',
						onClick: onCancel,
					},
					{
						variant: 'primary',
						label: 'Save and close',
						isSubmit: true,
						disabled: isSubmitting,
					},
				]}
			/>
			{isSubmitting && <SpinnerWithBackground />}
			<UnsavedChangesDialog isBlocked={isBlocked} confirmLeave={confirmLeave} cancelLeave={cancelLeave} />
		</form>
	)
}
